import { HttpClient } from '@angular/common/http';
import {
  inject,
  Injectable,
  isDevMode,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import {
  getBrowserLang,
  provideTransloco,
  Translation,
  TranslocoLoader,
  TranslocoService,
} from '@jsverse/transloco';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { provideTranslocoPreloadLangs } from '@jsverse/transloco-preload-langs';
import { firstValueFrom } from 'rxjs';
import * as en from '../../../public/assets/i18n/en.json';

type LeafKeys<T> = T extends object
  ? {
      [K in keyof T]-?: K extends string
        ? T[K] extends Array<unknown>
          ? K
          : T[K] extends object
            ? `${K}.${LeafKeys<T[K]>}`
            : K
        : never;
    }[keyof T]
  : never;

export type SupportedLang = 'en' | 'es' | 'fr' | 'pt' | 'ru' | 'ar';

export const TRANSLOCO_DEFAULT_LANG: SupportedLang = 'en';

export const TRANSLOCO_AVAILABLE_LANGS: SupportedLang[] = [
  'en',
  'es',
  'fr',
  // 'pt',
  'ru',
  'ar',
];

export type TranslationKeys = LeafKeys<typeof en>;

declare const __APP_VERSION__: string | undefined;

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string) {
    const version =
      typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : Date.now();
    const cacheBuster = isDevMode() ? '' : `?v=${version}`;

    return this.http.get<Translation>(
      `/assets/i18n/${lang}.json${cacheBuster}`,
    );
  }
}

export const provideUpuTransloco = () =>
  makeEnvironmentProviders([
    provideTransloco({
      config: {
        availableLangs: TRANSLOCO_AVAILABLE_LANGS,
        defaultLang: TRANSLOCO_DEFAULT_LANG,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        flatten: {
          aot: !isDevMode(),
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoPersistLang({
      getLangFn: (langs) => {
        const lang = langs.cachedLang ?? langs.browserLang ?? langs.defaultLang;

        if (!TRANSLOCO_AVAILABLE_LANGS.includes(lang as SupportedLang)) {
          return 'en';
        }

        return lang;
      },
      storage: {
        useValue: localStorage,
      },
    }),
    provideTranslocoPreloadLangs(
      (() => {
        let lang =
          localStorage.getItem('translocoLang') ||
          getBrowserLang() ||
          TRANSLOCO_DEFAULT_LANG;

        if (!TRANSLOCO_AVAILABLE_LANGS.includes(lang as SupportedLang)) {
          lang = 'en';
        }

        return [lang];
      })(),
    ),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);

      return firstValueFrom(transloco.load(transloco.getActiveLang()));
    }),
  ]);
